import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recurringService } from '@/services/recurringService';
import type { CreateRecurringInput, UpdateRecurringInput } from '@/types/recurring';

export function useRecurring() {
  return useQuery({
    queryKey: ['recurring'],
    queryFn: () => recurringService.getRecurring(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCreateRecurring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRecurringInput) => recurringService.createRecurring(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
    },
  });
}

export function useUpdateRecurring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRecurringInput }) =>
      recurringService.updateRecurring(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
    },
  });
}

export function useDeleteRecurring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringService.deleteRecurring(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
    },
  });
}

export function useProcessRecurring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringService.processRecurring(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

export default {
  useRecurring,
  useCreateRecurring,
  useUpdateRecurring,
  useDeleteRecurring,
  useProcessRecurring,
};
